import {
  Address,
  ClientError,
  DEFAULT_ERC20_DECIMALS,
  PERCENT_ZERO,
  ProtocolKey,
  ProtocolKeyService,
  ProtocolStateService,
  SiloData,
  SiloDataAsset,
} from "@silo/types";
import { Array, Effect, pipe } from "effect";
import { ContractsClient } from "../contracts-client";
import { getMarketMetaArray } from "../dashboard/get-market-meta-array";
import { getProtocolState } from "../protocol/get-protocol-state";
import { getProtocolConfig } from "@silo/config";
import { getSiloDataAsset } from "./get-silo-data-asset";
import { getSiloUserState } from "./get-silo-user-state";

export type GetSiloData = ReturnType<typeof getSiloData>;
export type GetSiloDataSuccess = Effect.Effect.Success<GetSiloData>;
export type GetSiloDataError = Effect.Effect.Error<GetSiloData>;
export type GetSiloDataContext = Effect.Effect.Context<GetSiloData>;

interface GetSiloDataParams {
  marketAddress: Address;
  user: Address | undefined;
}

export function getSiloData({ marketAddress, user }: GetSiloDataParams) {
  return Effect.gen(function* () {
    yield* Effect.logInfo(
      `Begin 🎬: get silo data market=${marketAddress} user=${user ?? "none"}`
    );

    const protocolKey = yield* ProtocolKeyService;

    const [marketMetaArray, protocolState] = yield* Effect.all(
      [getMarketMetaArray([marketAddress]), getProtocolState()],
      { concurrency: "unbounded" }
    );

    const marketMeta = marketMetaArray[0];

    if (!marketMeta) {
      return yield* Effect.fail(
        new ClientError({
          message: `Unknown market ${marketAddress} for protocol ${protocolKey}`,
        })
      );
    }

    const marketAssetMetaArray = pipe(
      [marketMeta.baseAsset, ...marketMeta.bridgeAssets],
      Array.flatMapNullable((asset) => asset)
    );

    const assetsEffect = pipe(
      Effect.all(
        marketAssetMetaArray.map((asset) =>
          getSiloDataAsset({ marketAssetMeta: asset, user })
        ),
        { concurrency: "unbounded" }
      ),
      Effect.provideService(ProtocolStateService, protocolState)
    );

    const userStateEffect = user
      ? getSiloUserState({ marketAddress, user })
      : Effect.succeed({
          userLtv: PERCENT_ZERO,
          userMaxLtv: PERCENT_ZERO,
          userLiquidationThreshold: PERCENT_ZERO,
        });

    const routerAddressEffect = getRouterAddressMemo(protocolKey);

    const [assets, userState, routerAddress] = yield* Effect.all(
      [assetsEffect, userStateEffect, routerAddressEffect],
      { concurrency: "unbounded" }
    );

    const { nativeRewards } = getProtocolConfig(protocolKey);
    const rewardTokenDecimals = nativeRewards?.decimals ?? DEFAULT_ERC20_DECIMALS;

    const hasPosition = getHasPosition(assets);

    const siloData: SiloData = {
      data: {
        protocolKey,
        marketAddress,
        routerAddress,
        user: user ?? null,
        quotePriceUsd: protocolState.quotePriceUsd,
        rewardTokenPriceUsd: protocolState.rewardTokenPriceUsd,
        rewardTokenDecimals,

        assets,

        userLtv: userState.userLtv,
        userMaxLtv: userState.userMaxLtv,
        userLiquidationThreshold: userState.userLiquidationThreshold,
        hasPosition,
      },
    };

    yield* Effect.logInfo(
      `Complete ✅: get silo data market=${marketAddress} assets=${assets.length}`
    );

    return siloData;
  });
}

function getHasPosition(assets: readonly SiloDataAsset[]): boolean {
  return assets.some(
    (asset) =>
      asset.collateralBalance !== 0n ||
      asset.protectedBalance !== 0n ||
      asset.debtBalance !== 0n
  );
}

/**
 * Router address never changes for a deployed SiloRepository. Cache by
 * protocolKey for the lifetime of the serverless instance to avoid one
 * eth_call per request.
 */
const routerAddressByProtocol = new Map<ProtocolKey, Address>();

function getRouterAddressMemo(protocolKey: ProtocolKey) {
  return Effect.gen(function* () {
    const cached = routerAddressByProtocol.get(protocolKey);

    if (cached) {
      return cached;
    }

    const address = yield* ContractsClient.getRouterAddress();
    routerAddressByProtocol.set(protocolKey, address);
    return address;
  });
}
